import { useCallback, useState } from 'react'

import { useAppSelector } from '../store/hooks'
import { selectAccountId } from '../store/auth/authSlice'
import { useGetMeQuery } from '../store/user/user'
import { useListOAuth2AppsQuery } from '../store/auth/auth'
import { useZocksAuth, useZocksQuota } from '../components/ScopedBlock/hooks'
import { useOAuthApps } from '../views/Integration/useOAuthApps'
import { useAppQuotaGate } from '../views/OAuth2App/useAppQuotaGate'
import { McpAppAccessProbe } from './McpAppAccessProbe'

interface UseMcpAppsAccessParams {
  skip?: boolean
}

export const useMcpAppsAccess = ({ skip = false }: UseMcpAppsAccessParams = {}) => {
  const accountId = useAppSelector(selectAccountId)
  const { hasScope } = useZocksAuth()
  const { hasQuota, validateQuotaValue, quotaSuccess, isFetching: quotaFetching } = useZocksQuota()
  const { isOAuthAppAllowed, accountIsLoading } = useOAuthApps()
  const { isAppQuotaSatisfied } = useAppQuotaGate()

  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')
  const canListOAuth2Apps = hasScope('**', 'oauth2_app:list', ['oauth2_app:admin'])

  const { data: oAuth2AppData, isFetching: appsFetching } = useListOAuth2AppsQuery({ accountId }, { skip: skip || !oauth2Enabled || !canListOAuth2Apps })
  const { data: currentUser, isFetching: meFetching } = useGetMeQuery({ accountId }, { skip })

  const mcpApps = (oAuth2AppData?.data ?? []).filter((app) => app.category === 'mcp' && isOAuthAppAllowed(app.id) && isAppQuotaSatisfied(app.clientId))
  const mcpClientIds = mcpApps.map((app) => app.clientId)

  const [accessByClientId, setAccessByClientId] = useState<Record<string, boolean>>({})

  const handleAccessResolve = useCallback((clientId: string, isAccessible: boolean) => {
    setAccessByClientId((previous) => (previous[clientId] === isAccessible ? previous : { ...previous, [clientId]: isAccessible }))
  }, [])

  const probes = mcpClientIds.map((clientId) => <McpAppAccessProbe key={clientId} clientId={clientId} onResolve={handleAccessResolve} />)
  const probesPending = mcpClientIds.some((clientId) => accessByClientId[clientId] === undefined)

  const mcpAppsAvailable = oauth2Enabled && mcpClientIds.some((clientId) => validateQuotaValue('integration.oauth2AppBlacklist', clientId))

  const mcpAppsEnabled = oauth2Enabled && mcpClientIds.some((clientId) => accessByClientId[clientId])

  const mcpClientIdSet = new Set(mcpClientIds)
  const hasMcpConnection = (currentUser?.integrations ?? []).some(
    (integration) => integration.type === 'oauth2_app' && mcpClientIdSet.has(integration.serviceId),
  )

  const isFetching = (!quotaSuccess && quotaFetching) || accountIsLoading || appsFetching || meFetching || probesPending

  return {
    mcpAppsAvailable,
    mcpAppsEnabled,
    hasMcpConnection,
    oauth2Enabled,
    isFetching,
    probes,
  }
}
